/*
 * SP Tools USA — Collection page UI affordances.
 *
 * Most of the filter sidebar markup (FILTERS heading, funnel icon, count badge,
 * RESET label, chip × close, summary caret, count pills, price Apply button,
 * price tier presets, pagination boundary arrows) is rendered server-side in
 * snippets/facets.liquid + snippets/pagination.liquid. This file owns the
 * runtime behaviors that can't live in Liquid:
 *
 *   1. Mobile filter drawer toggle (≤1023 px)
 *   2. Sort proxy — mirror toolbar <select> → Dawn's hidden facets sort <select>
 *   3. <details> open-state persistence across Dawn's AJAX swap (incl. first-visit
 *      default-close of any group labeled /rating/i so Rating starts collapsed)
 *   4. Price tier preset click → fill min/max inputs → re-submit form
 *   5. Em-dash separator between price min/max fields (can't reach between the two
 *      <div class="field">s without editing price-facet.liquid)
 *   6. Auto-apply mode — let Dawn's native input→debounced-submit run so any
 *      checkbox click applies instantly (no Apply button); flag aria-busy and
 *      auto-close the mobile drawer on change
 *   7. Mutation observer on #collectionSidebar so #4 + #6 re-run after Dawn's AJAX
 */
(function () {
  'use strict';

  var collectionHandle = (document.body.getAttribute('data-collection-handle') || location.pathname).replace(/[^a-z0-9-]/gi, '_');
  var STORAGE_KEY = 'spt:collection:' + collectionHandle + ':filters_closed';

  // Idempotent injection of the drawer-close (×) button. Dawn's renderActiveFacets
  // (facets.js:179-186) replaces .active-facets-desktop's innerHTML on every AJAX
  // apply, taking this button with it. Click is dispatched via delegation in
  // initFilterDrawer so the button only needs to exist in the DOM — no rebind.
  // Visibility on desktop is suppressed by CSS (template-collection.css default
  // display:none, overridden by inline-flex inside @media (max-width:1023px)).
  function ensureDrawerClose() {
    var sidebar = document.getElementById('collectionSidebar');
    if (!sidebar) return;
    var header = sidebar.querySelector('.active-facets-vertical-filter');
    if (!header || header.querySelector('.drawer-close')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'drawer-close';
    btn.setAttribute('aria-label', 'Close filters');
    btn.innerHTML = '<i class="ph-bold ph-x" aria-hidden="true"></i>';
    header.appendChild(btn);
  }

  /* ---------- 1. Mobile filter drawer toggle ---------- */
  function initFilterDrawer() {
    var toggle = document.getElementById('filterToggle');
    var sidebar = document.getElementById('collectionSidebar');
    if (!toggle || sidebar === null) return;
    // Inject close (×) on every call — Dawn's AJAX swap wipes it. The one-time
    // bindings (toggle handler, ESC trap, viewport listener) stay guarded by
    // sptDrawerBound; click goes through delegation below so re-injection
    // doesn't require re-binding.
    ensureDrawerClose();
    if (sidebar.dataset.sptDrawerBound === '1') return;
    sidebar.dataset.sptDrawerBound = '1';

    // Overlay is a CSS pseudo-element (.section-collection::before driven by
    // :has(.collection-sidebar.active)) — no JS DOM node. We catch backdrop
    // clicks via a document-level listener attached only while the drawer is open.

    var mql = window.matchMedia('(max-width: 1023px)');
    var lastTrigger = null;
    var keydownHandler = null;
    var backdropClickHandler = null;
    var savedScrollY = 0;

    // position:fixed scroll-lock — body.style.overflow='hidden' is unreliable
    // when <html> is the scroll container and iOS Safari ignores it outright.
    // Pinning the body at -scrollY freezes the page reliably; we restore the
    // exact scroll position on close so the user lands where they were.
    function lockBodyScroll() {
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = -savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    function unlockBodyScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, savedScrollY);
    }
    function isMobile() { return mql.matches; }

    // Dialog role + aria-modal are set in open()/close() so a closed sidebar at
    // mobile width isn't announced as a dialog. aria-label="Product filters" is
    // hardcoded in the section markup as fallback for aria-labelledby. With
    // auto-apply there's no Apply button to relabel per-viewport, so this is a
    // no-op kept as the viewport-change hook (drawer force-close lives in
    // handleViewportChange).
    function syncAriaForViewport() {}

    // Background chrome that should be blocked from focus + AT while the drawer
    // is open. inert is the modern, broadly-supported solution (Safari 15.5+,
    // Chrome 102+, Firefox 112+); body-scroll-lock alone doesn't block VO swipe.
    function gatherInertTargets() {
      var targets = [];
      var section = sidebar.closest('.section-collection');
      if (section && section.parentElement) {
        Array.prototype.forEach.call(section.parentElement.children, function (sib) {
          if (sib !== section) targets.push(sib);
        });
      }
      var toolbar = document.querySelector('.collection-toolbar');
      var main = document.querySelector('.collection-main');
      if (toolbar) targets.push(toolbar);
      if (main) targets.push(main);
      return targets;
    }
    var inertTargets = [];

    function getFocusable() {
      return Array.prototype.filter.call(
        sidebar.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
        function (el) {
          // offsetParent === null catches display:none / visibility:hidden chains;
          // good enough for the focus-trap loop where perfect accuracy isn't required.
          return el.offsetParent !== null || el === document.activeElement;
        }
      );
    }

    function trapTab(e) {
      if (e.key !== 'Tab') return;
      var focusables = getFocusable();
      if (!focusables.length) { e.preventDefault(); return; }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      if (!isMobile()) return;
      lastTrigger = document.activeElement;
      sidebar.classList.add('active');
      toggle.setAttribute('aria-expanded', 'true');
      sidebar.setAttribute('role', 'dialog');
      sidebar.setAttribute('aria-modal', 'true');
      sidebar.setAttribute('aria-label', 'Product filters');
      lockBodyScroll();

      // Block background chrome from focus + AT (VoiceOver/TalkBack swipe).
      inertTargets = gatherInertTargets();
      inertTargets.forEach(function (el) { el.inert = true; });

      // Defer focus to next frame so the slide-in transition has started — moving
      // focus instantly while the panel is still translateX(-100%) can cause
      // some browsers to scroll the page to find the off-screen target.
      // Re-query: the close button is re-injected after Dawn AJAX swaps, so the
      // node here may differ from the one present at init time.
      requestAnimationFrame(function () {
        var closeBtn = sidebar.querySelector('.drawer-close');
        if (closeBtn) closeBtn.focus();
      });

      keydownHandler = function (e) {
        if (e.key === 'Escape') {
          // Don't steal ESC if a higher-layered modal (e.g. cart drawer at z-index 1000) is open.
          var topModal = document.querySelector('.cart-drawer.active, dialog[open]');
          if (topModal && topModal !== sidebar) return;
          e.preventDefault();
          close();
          return;
        }
        trapTab(e);
      };
      document.addEventListener('keydown', keydownHandler);

      // Backdrop click = close. The CSS pseudo-element (.section-collection::before)
      // catches the click visually with pointer-events:auto; the event bubbles to
      // document with target===section-collection (or body). Any click whose target
      // is outside both the drawer and the toggle = backdrop or surrounding chrome.
      backdropClickHandler = function (e) {
        if (sidebar.contains(e.target)) return;
        if (toggle.contains(e.target)) return;
        close();
      };
      // Defer one tick so the click that opened the drawer doesn't immediately close it.
      setTimeout(function () {
        document.addEventListener('click', backdropClickHandler);
      }, 0);
    }

    function close() {
      sidebar.classList.remove('active');
      toggle.setAttribute('aria-expanded', 'false');
      sidebar.removeAttribute('role');
      sidebar.removeAttribute('aria-modal');
      sidebar.removeAttribute('aria-label');
      unlockBodyScroll();

      inertTargets.forEach(function (el) { el.inert = false; });
      inertTargets = [];

      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler);
        keydownHandler = null;
      }
      if (backdropClickHandler) {
        document.removeEventListener('click', backdropClickHandler);
        backdropClickHandler = null;
      }

      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus();
      } else if (toggle) {
        toggle.focus();
      }
      lastTrigger = null;
    }

    toggle.addEventListener('click', function () {
      if (sidebar.classList.contains('active')) close();
      else open();
    });

    // Delegated click — survives any future re-injection of .drawer-close.
    sidebar.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.drawer-close')) close();
    });

    // Viewport crosses 1024px while drawer is open → force-close so body scroll
    // lock + aria-modal don't leak into desktop mode.
    function handleViewportChange() {
      syncAriaForViewport();
      if (!isMobile() && sidebar.classList.contains('active')) {
        close();
      }
    }
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', handleViewportChange);
    else if (typeof mql.addListener === 'function') mql.addListener(handleViewportChange);

    // Initial sync — set aria + apply label for whichever viewport we loaded into.
    syncAriaForViewport();
  }

  /* ---------- 2. Sort proxy ---------- */
  function initSortProxy() {
    var proxy = document.querySelector('[data-sort-proxy]');
    if (!proxy) return;

    function findDawnSort() {
      return document.querySelector('.facets select[name="sort_by"], #SortBy, [id^="SortBy-"]');
    }

    proxy.addEventListener('change', function () {
      var dawnSort = findDawnSort();
      if (!dawnSort || dawnSort.value === proxy.value) return;
      dawnSort.value = proxy.value;
      dawnSort.dispatchEvent(new Event('input', { bubbles: true }));
      dawnSort.dispatchEvent(new Event('change', { bubbles: true }));
    });

    function syncFromDawn() {
      var dawnSort = findDawnSort();
      if (!dawnSort) return;
      if (proxy.value !== dawnSort.value) proxy.value = dawnSort.value;
    }

    syncFromDawn();
  }

  /* ---------- 3. <details> open-state persistence ---------- */
  function readClosedSet() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      return new Set(raw ? raw.split(',').filter(Boolean) : []);
    } catch (e) {
      return new Set();
    }
  }

  function writeClosedSet(set) {
    try {
      sessionStorage.setItem(STORAGE_KEY, Array.from(set).join(','));
    } catch (e) { /* quota / private mode — silently ignore */ }
  }

  function detailsKey(el) {
    var heading = el.querySelector('.facets__summary-label, .facets__display, summary');
    if (heading && heading.textContent) return heading.textContent.trim().slice(0, 64);
    return el.dataset.index || el.id || '';
  }

  function initDetailsState() {
    var sidebar = document.getElementById('collectionSidebar');
    if (!sidebar) return;

    // First visit (no sessionStorage entry yet) → default-close any group whose
    // label matches /rating/i to match the prototype's 4-open + Rating-closed state.
    var firstVisit = false;
    try { firstVisit = sessionStorage.getItem(STORAGE_KEY) === null; } catch (e) {}

    var closedSet = readClosedSet();

    if (firstVisit) {
      sidebar.querySelectorAll('.js-filter, details.facets__disclosure-vertical').forEach(function (d) {
        var key = detailsKey(d);
        if (key && /rating/i.test(key)) closedSet.add(key);
      });
      writeClosedSet(closedSet);
    }

    function applyState() {
      sidebar.querySelectorAll('.js-filter, details.facets__disclosure-vertical').forEach(function (d) {
        var key = detailsKey(d);
        if (!key) return;
        d.open = !closedSet.has(key);
      });
    }

    function trackToggles() {
      sidebar.querySelectorAll('.js-filter, details.facets__disclosure-vertical').forEach(function (d) {
        if (d.dataset.sptTracked) return;
        d.dataset.sptTracked = '1';
        d.addEventListener('toggle', function () {
          var key = detailsKey(d);
          if (!key) return;
          if (d.open) closedSet.delete(key);
          else closedSet.add(key);
          writeClosedSet(closedSet);
        });
      });
    }

    applyState();
    trackToggles();
  }

  /* ---------- 4. Price tier presets ----------
     The em-dash separator between MIN/MAX is now emitted by price-facet.liquid
     itself (vertical branch), so no JS injection needed. */
  function priceInputs(range) {
    return range.querySelectorAll('input[type="text"], input[type="number"]');
  }

  function bindPriceTierHandlers(sidebar) {
    sidebar.querySelectorAll('.price-tier-checkbox').forEach(function (cb) {
      if (cb.dataset.sptTierBound === '1') return;
      cb.dataset.sptTierBound = '1';
      cb.addEventListener('change', function () {
        var label = cb.closest('[data-tier-min]');
        if (!label) return;
        // The <ul.price-tier-presets> sits as a sibling AFTER <price-range> inside
        // the same .facets__display-vertical, so walk up to that wrapper, then back
        // down to the price-range.
        var displayParent = label.closest('.facets__display-vertical, .facets__display');
        var range = displayParent && displayParent.querySelector('price-range, .facets__price');
        if (!range) range = sidebar.querySelector('price-range, .facets__price');
        if (!range) return;
        var inputs = priceInputs(range);
        if (inputs.length < 2) return;

        if (cb.checked) {
          sidebar.querySelectorAll('.price-tier-checkbox').forEach(function (other) {
            if (other !== cb) other.checked = false;
          });
          inputs[0].value = label.getAttribute('data-tier-min') || '';
          inputs[1].value = label.getAttribute('data-tier-max') || '';
        } else {
          inputs[0].value = '';
          inputs[1].value = '';
        }

        inputs[0].dispatchEvent(new Event('input', { bubbles: true }));
        inputs[0].dispatchEvent(new Event('change', { bubbles: true }));
        inputs[1].dispatchEvent(new Event('input', { bubbles: true }));
        inputs[1].dispatchEvent(new Event('change', { bubbles: true }));

        // Dispatch a submit so Dawn's native onSubmitHandler applies the price
        // range via AJAX — same flow as clicking .price-apply. Without this the
        // filled min/max inputs sit there unapplied until the user submits.
        var form = document.getElementById('FacetFiltersForm');
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    });
  }

  // Compare an input value (rendered via money_without_currency, e.g. "100.00"
  // or "2,000.00") to a tier preset value (a plain integer string like "100"
  // or "2000"). Strip thousand-separators, parse as float, then compare with
  // a sub-cent tolerance. Both-empty matches; one-empty doesn't.
  function tierMatch(inputStr, tierStr) {
    inputStr = String(inputStr == null ? '' : inputStr).trim();
    tierStr = String(tierStr == null ? '' : tierStr).trim();
    if (inputStr === '' && tierStr === '') return true;
    if (inputStr === '' || tierStr === '') return false;
    var a = parseFloat(inputStr.replace(/,/g, ''));
    var b = parseFloat(tierStr.replace(/,/g, ''));
    if (isNaN(a) || isNaN(b)) return false;
    return Math.abs(a - b) < 0.005;
  }

  function syncPriceTiersFromInputs(sidebar) {
    sidebar.querySelectorAll('price-range, .facets__price').forEach(function (range) {
      var inputs = priceInputs(range);
      if (inputs.length < 2) return;
      var minVal = (inputs[0].value || '').trim();
      var maxVal = (inputs[1].value || '').trim();
      var displayParent = range.closest('.facets__display-vertical, .facets__display');
      if (!displayParent) return;
      var presets = displayParent.querySelector('.price-tier-presets');
      if (!presets) return;
      presets.querySelectorAll('.price-tier-checkbox').forEach(function (cb) {
        var label = cb.closest('[data-tier-min]');
        if (!label) return;
        cb.checked =
          tierMatch(minVal, label.getAttribute('data-tier-min')) &&
          tierMatch(maxVal, label.getAttribute('data-tier-max'));
      });
    });
  }

  function refreshSidebarRuntime() {
    var sidebar = document.getElementById('collectionSidebar');
    if (!sidebar) return;
    bindPriceTierHandlers(sidebar);
    syncPriceTiersFromInputs(sidebar);
  }

  /* ---------- 6. Auto-apply mode for the desktop filter form ----------
     Dawn's <facet-filters-form> (assets/facets.js:11) already auto-submits on
     input via an 800 ms debounce → onSubmitHandler → renderPage (AJAX swap +
     server-rendered chips). We let that native flow run untouched: a checkbox
     click applies the filter immediately with no Apply button.

     The only things we still own:
       - flag the region aria-busy while the AJAX swap is in flight (the
         mutation observer in #7 clears it once Dawn finishes rendering);
       - on mobile, close the filter drawer shortly after a change so the user
         sees the refreshed results instead of the panel that hid them.

     Price MIN/MAX still commit through the .price-apply submit button that
     price-facet.liquid renders — a range only makes sense once both bounds are
     entered, so instant-on-keystroke apply would be wrong there. Dawn's
     onSubmitHandler handles that submit natively too. */

  function markRegionBusy() {
    var grid = document.getElementById('ProductGridContainer');
    if (grid) grid.setAttribute('aria-busy', 'true');
    var status = document.getElementById('collection-loading-message');
    if (status) status.textContent = 'Loading products…';
  }

  function initAutoApplyMode() {
    var form = document.getElementById('FacetFiltersForm');
    if (!form || form.dataset.sptAutoApply === '1') return;
    form.dataset.sptAutoApply = '1';

    // Non-price checkbox/radio change → Dawn's debounced submit is already
    // queued; announce the pending update to AT and auto-close the mobile drawer.
    form.addEventListener('change', function (e) {
      var input = e.target;
      if (!input) return;
      if (input.type !== 'checkbox' && input.type !== 'radio') return;
      markRegionBusy();

      if (window.matchMedia('(max-width: 1023px)').matches) {
        var sidebar = document.getElementById('collectionSidebar');
        var toggle = document.getElementById('filterToggle');
        // Let Dawn's 800 ms debounce fire and the AJAX swap begin before we
        // close, so the results are already refreshing behind the drawer.
        setTimeout(function () {
          if (toggle && sidebar && sidebar.classList.contains('active')) toggle.click();
        }, 850);
      }
    });

    // Price submit (.price-apply) also flags busy so the spinner/announcement
    // fires; Dawn's native submit handler does the actual render.
    form.addEventListener('submit', function () {
      markRegionBusy();
    });
  }

  /* ---------- 7. Mutation observer — re-run runtime after Dawn's AJAX ---------- */
  function observeSidebarMutations(onSwap) {
    var sidebar = document.getElementById('collectionSidebar');
    if (!sidebar || typeof MutationObserver !== 'function') return;

    var debounceTimer = null;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(onSwap, 50);
          return;
        }
      }
    });

    observer.observe(sidebar, { childList: true, subtree: true });
  }

  function init() {
    initFilterDrawer();
    initSortProxy();
    initDetailsState();
    refreshSidebarRuntime();
    initAutoApplyMode();

    observeSidebarMutations(function () {
      var proxy = document.querySelector('[data-sort-proxy]');
      if (proxy) {
        var dawnSort = document.querySelector('.facets select[name="sort_by"], #SortBy, [id^="SortBy-"]');
        if (dawnSort && proxy.value !== dawnSort.value) proxy.value = dawnSort.value;
      }
      // Clear AT busy state + loading announcement — Dawn just finished rendering.
      // .collection-count (role="status" in the section markup) will announce
      // the new product count automatically; we just need to clear the loading text.
      var grid = document.getElementById('ProductGridContainer');
      if (grid) grid.removeAttribute('aria-busy');
      var status = document.getElementById('collection-loading-message');
      if (status) status.textContent = '';
      // Re-inject drawer-close (Dawn wipes it in renderActiveFacets). Dawn
      // re-runs its own <facet-filters-form> constructor on the swapped-in
      // section, so the native auto-submit is rebound automatically; we just
      // re-attach our busy/drawer-close listeners.
      ensureDrawerClose();
      initDetailsState();
      refreshSidebarRuntime();
      initAutoApplyMode();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
